import React, { useMemo } from 'react';
import { Flag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

export function MilestonesPanel({ milestones, planMode }) {
  const showDates = planMode === 'goals' || planMode === 'absolute' || planMode === 'conditions';
  const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '';

  // Live status per milestone — uses the milestone's `date` (preferred) or
  // `week` against today's date so the panel becomes a stakeholder-friendly
  // "where are we right now?" summary, not just a static schedule listing.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const projectStartMs = milestones?.find(m => (m?.name || '').toLowerCase().startsWith('project start'));
  const projectStartDate = projectStartMs?.date ? new Date(projectStartMs.date + 'T00:00:00') : null;
  const computeStatus = (m) => {
    // Pre-cutoff: any milestone whose date hasn't been reached yet → upcoming.
    // Cutoff +/- 0 days: in progress (covers ±3 days for week-granularity).
    // Past: complete.
    if (m?.date) {
      const d = new Date(m.date + 'T00:00:00');
      const diff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
      if (diff < -3) return 'upcoming';
      if (diff > 3) return 'complete';
      return 'in_progress';
    }
    if (Number.isFinite(m?.week) && projectStartDate) {
      const milestoneDate = new Date(projectStartDate);
      milestoneDate.setDate(milestoneDate.getDate() + (m.week - 1) * 7);
      const diff = Math.floor((today - milestoneDate) / (1000 * 60 * 60 * 24));
      if (diff < -3) return 'upcoming';
      if (diff > 3) return 'complete';
      return 'in_progress';
    }
    return null;
  };
  const statusStyles = {
    upcoming:    { label: 'Upcoming',    cls: 'bg-slate-100 text-slate-600 border-slate-200' },
    in_progress: { label: 'In progress', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    complete:    { label: 'Complete',    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  };

  // Per-milestone context label — Go-Live picks up the same
  // "X weeks from project start · Y weeks hypercare runway" intel
  // that the Gantt's flag tooltip surfaces, so users browsing the
  // panel see the same schedule story without hovering the chart.
  const contextLabels = useMemo(() => {
    const ms = milestones || [];
    const projectStart = ms.find(m => (m?.name || '').toLowerCase().startsWith('project start'));
    const hypercare = ms.find(m => (m?.name || '').toLowerCase().startsWith('hypercare'));
    const out = {};
    ms.forEach((m, i) => {
      const isGoLive = (m?.name || '').toLowerCase().startsWith('go live');
      if (!isGoLive) return;
      const parts = [];
      if (projectStart && Number.isFinite(projectStart.week) && Number.isFinite(m.week)) {
        const wks = m.week - projectStart.week;
        parts.push(`${wks}w from kickoff`);
      }
      if (hypercare && Number.isFinite(hypercare.week) && Number.isFinite(m.week)) {
        const slack = hypercare.week - m.week;
        parts.push(`${slack}w hypercare runway`);
      }
      if (parts.length) out[i] = parts.join(' · ');
    });
    return out;
  }, [milestones]);

  return (
    <Card data-testid="milestones-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-[#0F2040]">Project Milestones</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {milestones.map((m, i) => {
            const isGoLive = (m?.name || '').toLowerCase().startsWith('go live');
            const isWave = !!m?.is_wave;
            const status = computeStatus(m);
            const statusStyle = status ? statusStyles[status] : null;
            return (
              <div key={`${m.name}-${i}`}
                className={
                  isGoLive
                    ? 'border border-[#4ABFCF]/50 border-l-[3px] border-l-[#4ABFCF] rounded-md p-2.5 bg-gradient-to-br from-[#4ABFCF]/5 to-white shadow-[0_0_0_1px_rgba(74,191,207,0.15)]'
                    : isWave
                      ? 'border border-[#4ABFCF]/30 border-l-[3px] border-l-[#4ABFCF]/60 rounded-md p-2.5 bg-gradient-to-br from-[#4ABFCF]/[0.03] to-white'
                      : 'border border-slate-200 rounded-md p-2.5 bg-gradient-to-br from-white to-slate-50'
                }
                data-testid={isWave ? `wave-milestone-${i}` : `milestone-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  {isGoLive || isWave ? (
                    <Flag
                      className="w-3 h-3 text-[#0F2040] shrink-0"
                      strokeWidth={2.5}
                      fill={isGoLive ? '#4ABFCF' : 'rgba(74,191,207,0.5)'}
                      data-testid={`milestone-${i}-flag`}
                    />
                  ) : (
                    <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent border-t-[#F59E0B]" />
                  )}
                  <span className="text-[11px] font-semibold text-[#0F2040] truncate flex-1" title={m.name}>{m.name}</span>
                  {statusStyle && (
                    <span
                      className={`text-[8.5px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap ${statusStyle.cls}`}
                      data-testid={`milestone-${i}-status`}
                      data-status={status}
                    >
                      {statusStyle.label}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500">
                  Week {m.week}
                  {showDates && m.date && (
                    <span className="ml-1.5 text-slate-400 font-medium">· {fmtDate(m.date)}</span>
                  )}
                </div>
                {contextLabels[i] && (
                  <div
                    className="mt-1 text-[9.5px] font-medium text-[#0F2040] bg-[#4ABFCF]/10 border border-[#4ABFCF]/30 rounded px-1.5 py-0.5 leading-tight"
                    data-testid={`milestone-${i}-context`}
                  >
                    {contextLabels[i]}
                  </div>
                )}
                {m.note && (
                  <div
                    className="mt-1 text-[9.5px] italic text-slate-600 bg-amber-50/70 border border-amber-200/70 rounded px-1.5 py-0.5 leading-tight"
                    data-testid={`milestone-${i}-note`}
                    title={m.note}
                  >
                    {m.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
